import { Directive, ElementRef, EventEmitter, OnDestroy, OnInit, inject } from '@angular/core';
import { WRxBase } from './w-rx-base';
import { WBehaviorService } from './w-behavior.service';
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil, } from 'rxjs/operators';
import { observeNode } from './change-observer';
import { Subject, combineLatest, } from 'rxjs';
import { isBrowser } from './w-universal';
import { isEqualRect } from './resizable-container';

@Directive({
    standalone: false
})
export abstract class WAdaptiveBase extends WRxBase implements OnInit, OnDestroy {
    protected abstract elementRef: ElementRef<HTMLElement>;
    isULF = true;

    isMobile: boolean = false; // default, app can override the value in runtime
    _calledInit = false;
    _childInitCalled = false; // used to verify if child called super.ngOnInit
    bs = inject(WBehaviorService);

    protected _el!: HTMLElement;
    set baseElement(e: HTMLElement) {
        this._el = e;
    }

    private _bodyPosition = new Subject<DOMRect>();
    private _elementPosition = new Subject<DOMRect>();
    private _resize = this.fromEvent(window, 'resize').pipe(
        map(() => [window.innerWidth, window.innerHeight]),
        startWith([window.innerWidth, window.innerHeight]));

    get elementPosition() {
        return combineLatest([this._elementPosition, this._bodyPosition, this._resize]).pipe(
            takeUntil(this.destruction),
            // pass  if either body size changed or element rectangle changed; or window resized
            distinctUntilChanged(([e, b, [w, h]], [e1, b1, [w1, h1]]) => isEqualRect(e, e1) && isEqualRect(b, b1) && h === h1 && w === w1),
            // in the end return the current element rectangle (use the above just to trigger)
            map(() => this._el.getBoundingClientRect())
        );
    }
    private _stateResize = new EventEmitter<ResizeObserverEntry[]>();
    protected get stateResize() {
        return this._stateResize.pipe(takeUntil(this.destruction));
    }
    protected resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        this._stateResize.emit(entries);
    });
    constructor() {
        super();
        // Wrap child's ngOnInit to detect when it's called (vs when super.ngOnInit is called)
        // This allows us to warn only when child forgets super.ngOnInit, not when 
        // component is destroyed before ngOnInit runs
        const originalNgOnInit = this.ngOnInit;
        this.ngOnInit = () => {
            this._childInitCalled = true;
            originalNgOnInit.call(this);
        };
    }
    ngOnInit(): void {
        this._calledInit = true;
        this._el = this.elementRef.nativeElement;
        this.watchWindow();

        // do this only in browser
        if (isBrowser) {
            const nodeObserver = observeNode(document.body);
            nodeObserver.mutation.pipe(
                takeUntil(this.destruction),
                debounceTime(100) // we use this only as a trigger to check when something in page changes
            ).subscribe(this.checkPosition.bind(this));
        }
    }
    override ngOnDestroy(): void {
        super.ngOnDestroy();
        /* provided on destroy is called properly, we can verify if init calls parent method */
        if (this._childInitCalled && !this._calledInit) {
            const className = Object.getPrototypeOf(this).constructor.name;
            throw new Error(`super.ngOnInit() not called in ${className} or its parents`);
        }
        this.resizeObserver.disconnect();
    }
    /* configure service to use different rule for mobile */
    private watchWindow() {
        this.bs.mobile
            .pipe(
                takeUntil(this.destruction),
                distinctUntilChanged()
            )
            .subscribe((m: boolean) => {
                this.isMobile = m;
            });
    }
    /* observe changes */
    private checkPosition() {
        const rect = this._el.getBoundingClientRect();
        const bodyRect = document.body.getBoundingClientRect();
        this._elementPosition.next(rect);
        this._bodyPosition.next(bodyRect);
    }
}